import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { I18nProvider } from "@/lib/i18n";
import { Toaster } from "@/components/ui/sonner";

import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Documents from "@/pages/Documents";
import Operational from "@/pages/Operational";
import RoutesPage from "@/pages/RoutesPage";
import Evidences from "@/pages/Evidences";
import Audit from "@/pages/Audit";
import Financial from "@/pages/Financial";
import Commissions from "@/pages/Commissions";
import Notifications from "@/pages/Notifications";
import Reports from "@/pages/Reports";
import ClientPortal from "@/pages/ClientPortal";
import Settings from "@/pages/Settings";
import Tenants from "@/pages/Tenants";
import Webhooks from "@/pages/Webhooks";
import Security from "@/pages/Security";
import ImportHomologation from "@/pages/ImportHomologation";
import Productivity from "@/pages/Productivity";
import Goals from "@/pages/Goals";
import LiveTracking from "@/pages/LiveTracking";
import EmailIngestLog from "@/pages/EmailIngestLog";
import OccurrenceCatalog from "@/pages/OccurrenceCatalog";
import ServiceOrder from "@/pages/ServiceOrder";
import PublicTrack from "@/pages/PublicTrack";
import PublicLegal from "@/pages/PublicLegal";
import Users from "@/pages/Users";
import Sectors from "@/pages/Sectors";
import Bipagem from "@/pages/Bipagem";

function RoleHome() {
  const { user } = useAuth();
  if (user?.role === "deliverer") return <Navigate to="/documents" replace />;
  if (user?.role === "client") return <Navigate to="/portal" replace />;
  return <Navigate to="/dashboard" replace />;
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster richColors position="top-right" />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/rastreio/:number?" element={<PublicTrack />} />
              <Route path="/privacidade" element={<PublicLegal />} />
              <Route path="/termos" element={<PublicLegal />} />
              <Route path="/suporte" element={<PublicLegal />} />
              <Route path="/" element={<Protected><RoleHome /></Protected>} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/documents" element={<Protected><Documents /></Protected>} />
              <Route path="/operational" element={<Protected roles={["admin","operator"]}><Operational /></Protected>} />
              <Route path="/routes" element={<Protected roles={["admin","operator"]}><RoutesPage /></Protected>} />
              <Route path="/evidences" element={<Protected><Evidences /></Protected>} />
              <Route path="/audit" element={<Protected roles={["admin"]}><Audit /></Protected>} />
              <Route path="/financial" element={<Protected roles={["admin"]}><Financial /></Protected>} />
              <Route path="/commissions" element={<Protected><Commissions /></Protected>} />
              <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
              <Route path="/reports" element={<Protected roles={["admin","operator"]}><Reports /></Protected>} />
              <Route path="/portal" element={<Protected roles={["client","admin"]}><ClientPortal /></Protected>} />
              <Route path="/service-orders" element={<Protected roles={["deliverer","admin","operator"]}><ServiceOrder /></Protected>} />
              <Route path="/service-orders/:id" element={<Protected roles={["deliverer","admin","operator"]}><ServiceOrder /></Protected>} />
              <Route path="/settings" element={<Protected><Settings /></Protected>} />
              <Route path="/tenants" element={<Protected roles={["admin"]}><Tenants /></Protected>} />
              <Route path="/sectors" element={<Protected roles={["admin"]}><Sectors /></Protected>} />
              <Route path="/users" element={<Protected roles={["admin"]}><Users /></Protected>} />
              <Route path="/webhooks" element={<Protected roles={["admin","operator"]}><Webhooks /></Protected>} />
              <Route path="/imports" element={<Protected roles={["admin","operator"]}><ImportHomologation /></Protected>} />
              <Route path="/bipagem" element={<Protected roles={["admin","operator"]}><Bipagem /></Protected>} />
              <Route path="/productivity" element={<Protected roles={["admin","operator"]}><Productivity /></Protected>} />
              <Route path="/goals" element={<Protected roles={["admin"]}><Goals /></Protected>} />
              <Route path="/tracking" element={<Protected roles={["admin","operator"]}><LiveTracking /></Protected>} />
              <Route path="/email-ingest" element={<Protected roles={["admin","operator"]}><EmailIngestLog /></Protected>} />
              <Route path="/occurrences" element={<Protected roles={["admin"]}><OccurrenceCatalog /></Protected>} />
              <Route path="/security" element={<Protected><Security /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
